import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import moment from 'moment';
import { Table, Input, Button, Row, Col, Dropdown, Menu, Icon, message } from 'antd';
import qs from 'qs';
import Auth from '@aws-amplify/auth';
import { Segment, isManualVehicleVrm } from '@fxtr/i18n';

import config from '$config';
import { api } from '$app/api';
import Loading from '$app/shared/components/Loading';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';

import {
  getStatusSelectFilterOptions,
  getSelectedStatusValue,
} from '$app/shared/components/StatusSelectFilter/filterOptions';
import DateRangeFilter from '$app/shared/components/DateRangeFilter';
import StatusSelectFilter from '$app/shared/components/StatusSelectFilter';
import Status from './components/Status';
import { GARAGE_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import getBookingStatusName from '../../shared/getBookingStatusName';
import SegmentFilter from '$app/shared/components/SegmentFilter';
import { FLEET_BOOKING_SOURCES, UTM_CAMPAIGNS, UTM_MEDIUMS, UTM_SOURCES, BOOKING_LIST_PAGE_SIZE } from './constants';
import { CreateBookingSubmenu, CreteBookingMenu } from './styled';
import { query } from './queries';
import { getSavedFiltersFromLocalStorage, saveFiltersInLocalStorage } from '../../../common/helpers';

const collectionDelivery = (data) => {
  if (!data) return '-';
  const { collectionDateTimeLocal, deliveryDateTimeLocal } = data;
  const collection = moment(collectionDateTimeLocal);
  const delivery = moment(deliveryDateTimeLocal);

  if (collection.isSame(delivery, 'day'))
    return `${collection.format('DD MMM YYYY, h:mm a')} -> ${delivery.format('h:mm a')}`;
  return `${collection.format('DD MMM YYYY, h:mm a')} -> ${delivery.format('DD MMM, h:mm a')}`;
};

const isPreProd = ['development', 'staging'].includes(config.env);

const columns = [
  {
    title: 'Id',
    dataIndex: 'bookingId',
    sortIndex: 'bookingId.keyword',
    key: 'bookingId',
    sorter: true,
    width: '10%',
    render: (bookingId, booking) => {
      const flag = booking.segment === Segment.Fixter_FR ? '🇫🇷' : '🇬🇧';
      return `${flag} ${bookingId}`;
    },
  },
  {
    title: 'Customer',
    sortIndex: 'firstName.keyword',
    key: 'fullname',
    sorter: true,
    width: '15%',
    render: (row) => `${capitalize(row.firstName)} ${capitalize(row.lastName)}`,
  },
  {
    title: 'Vehicle',
    dataIndex: 'vrm',
    sortIndex: 'vrm.keyword',
    key: 'vehicle',
    sorter: true,
    width: '15%',
    render: (vrm) => (isManualVehicleVrm(vrm) ? '' : vrm),
  },
  {
    title: 'Pick Up / Drop Off',
    key: 'schedule',
    sortIndex: 'collectionDateTimeLocal',
    sorter: true,
    render: (booking) => collectionDelivery(booking),
    width: '20%',
  },
  {
    title: 'Garage',
    key: 'garage',
    sortIndex: 'garageName.keyword',
    sorter: true,
    align: 'center',
    dataIndex: 'garageName',
    width: '15%',
  },
  {
    title: 'Progress',
    dataIndex: 'masterStatusName',
    key: 'masterStatusName',
    sortIndex: 'masterStatusName.keyword',
    sorter: true,
    render: (masterStatusName, booking) => {
      const statusComponent = (
        <Status masterStatusName={getBookingStatusName(booking)} style={{ alignSelf: 'end' }}>
          {getBookingStatusName(booking)}
        </Status>
      );

      const mapStatusToImage = {
        WORK_COMPLETE: '/assets/images/awaiting_payment.png',
        PAID: '/assets/images/payment_succeded.png',
        UNPAID: '/assets/images/payment_failed.png',
        DISPUTE: '/assets/images/payment_failed.png',
        CLOSED: '/assets/images/payment_succeded.png',
      };

      const mapTagsToImage = (currentBooking) => {
        const tags = currentBooking.tags || [];
        if (tags.includes('stayover')) return '/assets/images/stayover.svg';
        return null;
      };

      const progressImage = mapStatusToImage[masterStatusName] || mapTagsToImage(booking) || null;

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: progressImage ? 'space-between' : 'flex-end',
          }}
        >
          {progressImage ? <img height="22px" style={{ marginRight: '8px' }} src={progressImage} /> : null}
          {statusComponent}
        </div>
      );
    },
    width: '20%',
  },
];

if (isPreProd) {
  columns.push({
    title: 'C&D',
    key: 'cndType',
    sortIndex: 'collectionDeliveryOption',
    sorter: true,
    render: ({ collectionDeliveryOption }) => {
      if (collectionDeliveryOption === 'CustomerC&D') return 'Customer';
      if (collectionDeliveryOption === 'GarageC&D') return 'Garage';
      return collectionDeliveryOption;
    },
  });
}

const dateFormat = 'YYYY-MM-DD';
const BOOKING_LIST_FILTERS = 'bookingListFilters';

const initialFilters = {
  dateRangeFrom: null,
  dateRangeTo: null,
  must: [],
  mustNot: [],
  selectedId: null,
};

const initialPagination = {
  from: 0,
  total: 0,
  page: 1,
  limit: BOOKING_LIST_PAGE_SIZE,
};

const getSavedBookingListFilters = () => {
  try {
    return getSavedFiltersFromLocalStorage(BOOKING_LIST_FILTERS);
  } catch (error) {
    localStorage.removeItem(BOOKING_LIST_FILTERS);
    // eslint-disable-next-line no-console
    console.error('Could not read saved booking list filters', error);
    return {};
  }
};

export const getStatusFromSavedFilters = (filters) => {
  if (!filters.selectedId) return 'none';

  return getSelectedStatusValue(filters.selectedId) || 'none';
};

export const getDateRangeFromSavedFilters = (filters) => [
  filters.dateRangeFrom ? moment(filters.dateRangeFrom, dateFormat) : null,
  filters.dateRangeTo ? moment(filters.dateRangeTo, dateFormat) : null,
];

export const getInitialBookingListState = (savedFilters = {}) => {
  const filters = {
    ...initialFilters,
    ...savedFilters.filters,
  };

  return {
    segment: savedFilters.segment || 'all',
    status: getStatusFromSavedFilters(filters),
    dateRange: getDateRangeFromSavedFilters(filters),
    searchTerm: savedFilters.searchTerm || '',
    isFetching: true,
    filters,
    pagination: {
      ...initialPagination,
      ...savedFilters.pagination,
      total: 0,
    },
    sort: savedFilters.sort || {},
    list: null,
    authUser: null,
  };
};

class BookingsListPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = getInitialBookingListState(getSavedBookingListFilters());
  }

  async componentDidMount() {
    await Promise.all([this.loadAuthUser(), this.loadBookings()]);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, pagination, searchTerm, segment, sort } = this.state;

    if (
      filters !== prevState.filters ||
      pagination !== prevState.pagination ||
      searchTerm !== prevState.searchTerm ||
      segment !== prevState.segment ||
      sort !== prevState.sort
    ) {
      saveFiltersInLocalStorage(BOOKING_LIST_FILTERS, {
        segment,
        searchTerm,
        filters,
        pagination: {
          from: pagination.from,
          page: pagination.page,
          limit: pagination.limit,
        },
        sort,
      });
    }
  }

  get pagination() {
    const { pagination } = this.state;
    return {
      current: pagination.page,
      total: pagination.total,
      pageSize: pagination.limit,
    };
  }

  get list() {
    const { list } = this.state;
    return (list || []).map((item) => ({ ...item, key: item.bookingId }));
  }

  get selectDefaultValue() {
    const { filters } = this.state;
    const selectedId = filters.selectedId || [];
    const option = getSelectedStatusValue(selectedId);
    if (option) return option.id;
    return '';
  }

  loadAuthUser = async () => {
    try {
      const amplifyUser = await Auth.currentAuthenticatedUser({ bypassCache: false });
      this.setState({
        authUser: {
          username: amplifyUser.username,
          email: amplifyUser.attributes?.email,
          name: amplifyUser.attributes?.given_name,
          surname: amplifyUser.attributes?.family_name,
        },
      });
    } catch (error) {
      message.error(get(error, 'response.data.message', error.message));
    }
  };

  loadBookings = async () => {
    this.setState({ isFetching: true });

    try {
      const { filters, pagination, searchTerm, sort } = this.state;
      const { data } = await api.post('/bookings/table2', {
        from: pagination.from,
        size: pagination.limit,
        sort: Object.keys(sort).length ? sort : { bookingId: 'desc' },
        query: query({
          query: searchTerm,
          dateRangeFrom: filters.dateRangeFrom,
          dateRangeTo: filters.dateRangeTo,
          must: filters.must,
          mustNot: filters.mustNot,
        }),
      });

      this.setState((state) => ({
        ...state,
        list: data.data.map((row) => (row._source ? row._source : row)),
        isFetching: false,
        pagination: {
          ...state.pagination,
          total: data.recordsTotal,
        },
      }));
    } catch (error) {
      this.setState({ isFetching: false });
      message.error(get(error, 'response.data.message', error.message));
    }
  };

  handleSearchChange = (evt) => {
    let term = evt.target.value;
    const masterToGarageStatus = {
      QUOTING_EXTRA_WORK: GARAGE_STATUS.DRAFT,
      TO_REVIEW: GARAGE_STATUS.FIXTER_REVIEW,
      CUSTOMER_REVIEWING: GARAGE_STATUS.CUSTOMER_REVIEW,
    }[term.toUpperCase()];

    if (masterToGarageStatus) {
      term = masterToGarageStatus;
    }

    this.setState({ searchTerm: term });
  };

  handleSearchSubmit = async () => {
    await this.setStateAsync((state) => ({
      pagination: {
        ...state.pagination,
        page: 1,
        from: 0,
      },
    }));
    await this.loadBookings();
  };

  handleDateRangeChange = async ([from, to]) => {
    await this.setStateAsync((state) => ({
      dateRange: [from, to],
      filters: {
        ...state.filters,
        dateRangeFrom: from ? from.format(dateFormat) : null,
        dateRangeTo: to ? to.format(dateFormat) : null,
      },
      pagination: {
        ...state.pagination,
        page: 1,
        from: 0,
      },
    }));
    await this.loadBookings();
  };

  handleFilterChange = async (selectedId) => {
    if (selectedId === 'none') {
      await this.setStateAsync((state) => ({
        status: 'none',
        filters: {
          ...state.filters,
          selectedId: null,
          must: [],
          mustNot: [],
        },
        pagination: {
          ...state.pagination,
          page: 1,
          from: 0,
        },
      }));
      await this.loadBookings();
      return;
    }

    const selected = get(getStatusSelectFilterOptions(), selectedId);
    let { must } = selected;
    if (this.state.segment === 'all') {
      must = [...must];
    } else {
      must = [...must, { 'segment.keyword': this.state.segment }];
    }

    await this.setStateAsync((state) => ({
      status: selected,
      filters: {
        ...state.filters,
        selectedId,
        must,
        mustNot: selected.mustNot,
      },
      pagination: {
        ...state.pagination,
        page: 1,
        from: 0,
      },
    }));
    await this.loadBookings();
  };

  handleTableChange = async (pagination, filters, sorter) => {
    const nextSort = sorter.column
      ? {
          [get(sorter, 'column.sortIndex')]: sorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : this.state.sort;

    await this.setStateAsync((state) => ({
      sort: nextSort,
      pagination: {
        ...state.pagination,
        page: pagination.current || state.pagination.page,
        from: ((pagination.current || state.pagination.page) - 1) * state.pagination.limit,
      },
    }));
    await this.loadBookings();
  };

  handleRowClick = (record) => {
    const { history } = this.props;
    return {
      onClick: () => {
        history.push(`/${record.bookingId}`);
      },
    };
  };

  handleClearFilters = async () => {
    await this.setStateAsync((state) => ({
      ...state,
      segment: 'all',
      status: 'none',
      dateRange: [null, null],
      searchTerm: '',
      filters: initialFilters,
      pagination: {
        ...initialPagination,
        total: state.pagination.total,
        page: 1,
        from: 0,
      },
      sort: {},
    }));
    localStorage.removeItem(BOOKING_LIST_FILTERS);
    await this.loadBookings();
  };

  handleSegmentChange = async (segment) => {
    if (segment === 'all') {
      await this.handleClearFilters();
      return;
    }

    await this.setStateAsync((state) => ({
      segment,
      status: 'none',
      filters: {
        ...state.filters,
        selectedId: 'segment.keyword',
        must: [{ 'segment.keyword': segment }],
        mustNot: [],
      },
      pagination: {
        ...state.pagination,
        page: 1,
        from: 0,
      },
    }));

    await this.loadBookings();
  };

  setStateAsync = (updater) =>
    new Promise((resolve) => {
      this.setState(updater, resolve);
    });

  render() {
    const { authUser, isFetching, searchTerm, segment, status, dateRange } = this.state;

    const onCreateBooking = (segmentValue, source, medium, campaign) => {
      if (!authUser) return;
      if (!UTM_SOURCES.includes(source) || !UTM_MEDIUMS.includes(medium) || !UTM_CAMPAIGNS.includes(campaign)) {
        return;
      }
      window.open(
        `${segmentValue === Segment.Fixter_UK ? config.landingUrl : config.landingUrlFr}?${qs.stringify({
          utm_source: source,
          utm_medium: medium,
          utm_campaign: campaign,
          utm_content: authUser.username,
        })}`,
        '_blank'
      );
    };

    if (!this.list) return <Loading />;

    return (
      <Container>
        <Header>
          <Row justify="space-around" align="middle">
            <Col span={5} style={{ minWidth: 'max-content' }}>
              <Title style={{ marginBottom: '16px' }}>Bookings</Title>
              <Dropdown
                overlay={
                  <CreteBookingMenu>
                    <Menu.SubMenu
                      key="new_booking"
                      title={
                        <>
                          Phone sales booking -
                          <br />
                          new customers only
                        </>
                      }
                    >
                      <CreateBookingSubmenu
                        key="phone_booking_uk"
                        onClick={() => onCreateBooking(Segment.Fixter_UK, 'call', 'sales', 'new_booking')}
                      >
                        UK
                      </CreateBookingSubmenu>
                      <CreateBookingSubmenu
                        key="phone_booking_fr"
                        onClick={() => onCreateBooking(Segment.Fixter_FR, 'call', 'sales', 'new_booking')}
                      >
                        France
                      </CreateBookingSubmenu>
                    </Menu.SubMenu>
                    <Menu.SubMenu key="fleet_booking" title="Fleet booking">
                      {FLEET_BOOKING_SOURCES.map(({ key, text, utmSource }) => (
                        <CreateBookingSubmenu
                          key={key}
                          onClick={() => onCreateBooking(Segment.Fixter_UK, utmSource, 'fleets', 'fleet_booking')}
                        >
                          {text}
                        </CreateBookingSubmenu>
                      ))}
                    </Menu.SubMenu>
                  </CreteBookingMenu>
                }
                trigger={['click']}
                placement="bottomLeft"
              >
                <Button type="primary">
                  Create a booking <Icon type="down" />
                </Button>
              </Dropdown>
            </Col>
            <Col span={19} style={{ textAlign: 'right' }}>
              <SegmentFilter value={segment} onChange={this.handleSegmentChange} style={{ width: 220 }} />
              <StatusSelectFilter onChange={this.handleFilterChange} value={status} style={{ width: 200 }} />
              <DateRangeFilter onChange={this.handleDateRangeChange} value={dateRange} style={{ width: 200 }} />
              <div className={`filter-container ${searchTerm ? 'active' : ''}`} style={{ width: 200 }}>
                <Input.Search
                  placeholder="Id, customer, vehicle, progress..."
                  onSearch={this.handleSearchSubmit}
                  onChange={this.handleSearchChange}
                  style={{ width: 170 }}
                  value={searchTerm}
                />
              </div>
              <Button onClick={this.handleClearFilters} className="reset-filters">
                Reset
              </Button>
            </Col>
          </Row>
        </Header>
        <Table
          size="medium"
          onChange={this.handleTableChange}
          dataSource={this.list}
          columns={columns}
          onRow={this.handleRowClick}
          loading={isFetching}
          pagination={this.pagination}
        />
        {this.pagination.total} entries
      </Container>
    );
  }
}

export default withRouter(BookingsListPage);
